import React, { useEffect, useState, useCallback } from 'react';
import { useParams, Link } from 'react-router-dom';
import api from '../../api/client';
import { useAuth } from '../../contexts/AuthContext';
import Button from '../../components/Button';
import BracketView from '../../components/BracketView';
import MatchModal from '../../components/MatchModal';
import FormField, { Input, Select } from '../../components/FormField';
import Tooltip from '../../components/Tooltip';
import TeamHoverName from '../../components/TeamHoverName';
import { FORMAT_DESCRIPTIONS } from '../../utils/tournamentFormats';
import { getRoundLabel } from '../../utils/bracketUtils';

const FORMAT_LABELS = {
  single_elimination: 'Single Elimination',
  double_elimination: 'Double Elimination',
  ffa_scoreboard: 'FFA-Scoreboard',
  seeded_single_elimination: 'Seeded Single Elimination',
  battle_royale: 'Battle Royale',
  time_trial: 'Zeitfahren',
};

const PLACEMENT_POINTS = { 1: 10, 2: 7, 3: 5, 4: 3 };
function calcPoints(rank) {
  if (PLACEMENT_POINTS[rank]) return PLACEMENT_POINTS[rank];
  if (rank <= 8) return 1;
  return 0;
}

// ── FFA Admin ─────────────────────────────────────────────────────────────────

const STATUS_BADGE = {
  upcoming: { label: 'Ausstehend', cls: 'bg-slate-100 text-slate-500' },
  open:     { label: 'Offen',      cls: 'bg-amber-100 text-amber-700' },
  completed:{ label: 'Abgeschlossen', cls: 'bg-emerald-100 text-emerald-700' },
};

function FragsForm({ label, participantTeams, initialNote, onSave, saving }) {
  const [frags, setFrags] = useState(() => {
    const init = {};
    participantTeams.forEach((t) => { init[t.id] = ''; });
    return init;
  });
  const [note, setNote] = useState(initialNote || '');

  const ranked = [...participantTeams]
    .filter((t) => frags[t.id] !== '')
    .sort((a, b) => parseInt(frags[b.id] || 0) - parseInt(frags[a.id] || 0));

  const submit = () => {
    const entries = participantTeams.map((t) => ({ team_id: t.id, frags: parseInt(frags[t.id] || 0) }));
    onSave({ note: note.trim() || null, scores: entries });
  };

  return (
    <div className="mt-3 pt-3 border-t border-slate-100">
      <p className="text-xs font-semibold text-slate-500 mb-3 uppercase tracking-widest">{label}</p>
      <div className="space-y-2 mb-4">
        {participantTeams.map((t) => {
          const rank = ranked.findIndex((r) => r.id === t.id) + 1 || null;
          const pts = rank ? calcPoints(rank) : null;
          return (
            <div key={t.id} className="flex items-center gap-3">
              <span className="text-sm font-medium text-slate-700 w-36 truncate">{t.name}</span>
              <Input
                type="number" min="0" className="w-24 text-center"
                placeholder="Frags"
                value={frags[t.id] || ''}
                onChange={(e) => setFrags({ ...frags, [t.id]: e.target.value })}
              />
              {rank && (
                <span className="text-xs text-slate-500">
                  Platz {rank} → <span className="font-bold text-ocean-700">{pts} Pkt</span>
                </span>
              )}
            </div>
          );
        })}
      </div>
      <div className="mb-4">
        <label className="text-xs font-medium text-slate-500 block mb-1">Notiz / Map (optional)</label>
        <Input
          value={note}
          onChange={(e) => setNote(e.target.value)}
          placeholder="z.B. Dust2, Mirage, ..."
          className="max-w-xs"
        />
      </div>
      <Button size="sm" loading={saving} onClick={submit}>Ergebnisse speichern</Button>
    </div>
  );
}

function StandingsTable({ standings, finalistIds }) {
  return (
    <table className="w-full text-sm">
      <thead>
        <tr className="text-xs text-slate-400 border-b border-slate-100">
          <th className="text-left py-1 pr-3 font-medium">#</th>
          <th className="text-left py-1 font-medium">Spieler</th>
          <th className="text-right py-1 font-medium">Punkte</th>
          <th className="text-right py-1 font-medium">Frags</th>
        </tr>
      </thead>
      <tbody>
        {standings.map((s) => {
          const isFinalist = finalistIds && finalistIds.has(s.team_id);
          const isCutoff = finalistIds && isFinalist &&
            standings.find(x => !finalistIds.has(x.team_id) && x.rank === s.rank + 1);
          return (
            <tr key={s.team_id}
              className={`border-b border-slate-50 ${isCutoff ? 'border-b-2 border-b-ocean-300' : ''}`}>
              <td className="py-1.5 pr-3 font-bold text-ocean-700">{s.rank}</td>
              <td className={`py-1.5 font-medium ${isFinalist !== undefined ? (isFinalist ? 'text-slate-800' : 'text-slate-400') : 'text-slate-700'}`}>
                <TeamHoverName teamId={s.team_id} name={s.team_name} />
                {isFinalist && finalistIds && (
                  <span className="ml-2 text-xs text-ocean-500 font-semibold">→ Finale</span>
                )}
              </td>
              <td className="py-1.5 text-right font-bold text-sand-600">{s.total_points}</td>
              <td className="py-1.5 text-right text-slate-500">{s.total_frags}</td>
            </tr>
          );
        })}
      </tbody>
    </table>
  );
}

function RoundNoteInline({ tournamentId, round, onUpdated }) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(round.note || '');
  const [saving, setSaving] = useState(false);

  const save = async () => {
    setSaving(true);
    try {
      await api.put(`/api/admin/tournaments/${tournamentId}/ffa/rounds/${round.id}/note`, { note: draft.trim() || null });
      if (onUpdated) onUpdated();
      setEditing(false);
    } finally { setSaving(false); }
  };

  if (editing) {
    return (
      <div className="flex gap-1 items-center mt-1">
        <Input
          autoFocus value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onKeyDown={(e) => { if (e.key === 'Enter') save(); if (e.key === 'Escape') setEditing(false); }}
          placeholder="Map / Notiz..."
          className="text-xs py-0.5 max-w-xs"
        />
        <button onClick={save} disabled={saving}
          className="text-xs bg-ocean-500 text-white px-2 py-1 rounded hover:bg-ocean-600 disabled:opacity-50">
          {saving ? '…' : '✓'}
        </button>
        <button onClick={() => { setDraft(round.note || ''); setEditing(false); }}
          className="text-xs text-slate-400 hover:text-slate-600 px-1">✕</button>
      </div>
    );
  }
  return (
    <div className="flex items-center gap-1 mt-1 cursor-pointer group" onClick={() => setEditing(true)}>
      {round.note
        ? <span className="text-xs text-slate-500 bg-slate-100 px-2 py-0.5 rounded group-hover:bg-slate-200 transition">🗺 {round.note}</span>
        : <span className="text-xs text-slate-300 group-hover:text-ocean-400 transition">+ Map / Notiz</span>}
    </div>
  );
}

function FFARoundCard({ tournamentId, round, phaseLabel, participantTeams, onRefresh, savingId, onSaveScores }) {
  const badge = STATUS_BADGE[round.status] || STATUS_BADGE.upcoming;

  return (
    <div className={`card border ${round.status === 'open' ? 'border-amber-300 bg-amber-50/30' : 'border-slate-100'}`}>
      {/* Header */}
      <div className="flex items-start justify-between gap-2 mb-1">
        <div>
          <div className="flex items-center gap-2">
            <span className="text-sm font-bold text-slate-700">
              {phaseLabel} — Runde {round.round_number}
            </span>
            <span className={`text-[10px] font-bold uppercase px-2 py-0.5 rounded-full ${badge.cls}`}>
              {badge.label}
            </span>
          </div>
          <RoundNoteInline tournamentId={tournamentId} round={round} onUpdated={onRefresh} />
        </div>
        {round.status === 'completed' && (
          <button
            onClick={() => { if (window.confirm('Ergebnisse dieser Runde zurücksetzen?')) onSaveScores(round.id, null); }}
            className="text-xs text-slate-400 hover:text-red-500 transition shrink-0">
            Zurücksetzen
          </button>
        )}
      </div>

      {/* Scores table (completed) */}
      {round.status === 'completed' && round.scores.length > 0 && (
        <table className="w-full text-xs mt-2">
          <thead>
            <tr className="text-slate-400 border-b border-slate-100">
              <th className="text-left py-1 font-medium">Platz</th>
              <th className="text-left py-1 font-medium">Spieler</th>
              <th className="text-right py-1 font-medium">Frags</th>
              <th className="text-right py-1 font-medium">Punkte</th>
            </tr>
          </thead>
          <tbody>
            {round.scores.map((s) => (
              <tr key={s.team_id} className="border-b border-slate-50">
                <td className="py-1 font-bold text-slate-600">#{s.placement}</td>
                <td className="py-1 text-slate-700"><TeamHoverName teamId={s.team_id} name={s.team_name} /></td>
                <td className="py-1 text-right text-slate-600">{s.frags}</td>
                <td className="py-1 text-right font-bold text-ocean-600">{s.points}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      {/* Score entry form (open) */}
      {round.status === 'open' && (
        <FragsForm
          label="Frags eintragen"
          participantTeams={participantTeams}
          initialNote={round.note}
          saving={savingId === round.id}
          onSave={(payload) => onSaveScores(round.id, payload)}
        />
      )}
    </div>
  );
}

function FFAAdmin({ tournament, teams, onRefresh }) {
  const [ffaData, setFfaData] = useState(null);
  const [savingId, setSavingId] = useState(null);
  const [initializing, setInitializing] = useState(false);
  const [error, setError] = useState('');

  const loadFfa = useCallback(() => {
    api.get(`/api/tournaments/${tournament.id}/ffa`)
      .then((r) => setFfaData(r.data))
      .catch(() => {});
  }, [tournament.id]);

  useEffect(() => { loadFfa(); }, [loadFfa]);

  const handleSaveScores = async (roundId, payload) => {
    if (!payload) {
      // Reset
      if (!window.confirm('Ergebnisse zurücksetzen?')) return;
      try {
        await api.delete(`/api/admin/tournaments/${tournament.id}/ffa/rounds/${roundId}/scores`);
        loadFfa(); onRefresh();
      } catch (err) { setError(err.response?.data?.detail || 'Fehler'); }
      return;
    }
    setSavingId(roundId); setError('');
    try {
      await api.post(`/api/admin/tournaments/${tournament.id}/ffa/rounds/${roundId}/scores`, payload);
      loadFfa(); onRefresh();
    } catch (err) { setError(err.response?.data?.detail || 'Fehler'); }
    finally { setSavingId(null); }
  };

  const initializeRounds = async () => {
    setInitializing(true); setError('');
    try {
      await api.post(`/api/admin/tournaments/${tournament.id}/ffa/initialize-rounds`);
      loadFfa();
    } catch (err) { setError(err.response?.data?.detail || 'Fehler'); }
    finally { setInitializing(false); }
  };

  if (!ffaData) return <div className="text-slate-400 text-sm py-8 text-center">Lade...</div>;

  const { ffa_quali_rounds, ffa_final_generated, quali_rounds, quali_standings,
          finalists, final_round, final_standings } = ffaData;

  const completedCount = quali_rounds.filter(r => r.status === 'completed').length;
  const finalistIds = ffa_final_generated ? new Set((finalists || []).map(f => f.team_id)) : null;
  const finalistTeams = teams.filter(t => finalistIds && finalistIds.has(t.id));

  return (
    <div className="space-y-6">
      {error && <div className="alert-error">{error}</div>}

      {/* Init button for old tournaments without pre-created rounds */}
      {quali_rounds.length === 0 && (
        <div className="card border-2 border-dashed border-slate-200 text-center py-6">
          <p className="text-sm text-slate-500 mb-3">Noch keine Runden vorhanden.</p>
          <Button size="sm" loading={initializing} onClick={initializeRounds}>
            {ffa_quali_rounds} Runden initialisieren
          </Button>
        </div>
      )}

      {/* ── Phase 1: Qualifikation ── */}
      {quali_rounds.length > 0 && (
        <div className="rounded-xl border border-ocean-200 overflow-hidden">
          <div className="bg-ocean-600 px-4 py-3 flex items-center justify-between">
            <span className="text-white font-bold text-sm">Phase 1 — Qualifikation</span>
            <span className="text-ocean-200 text-xs">{completedCount} / {ffa_quali_rounds} Runden</span>
          </div>
          <div className="p-4 space-y-3 bg-white">
            {/* Progress bar */}
            <div className="h-1.5 bg-slate-100 rounded-full overflow-hidden">
              <div className="h-full bg-ocean-500 rounded-full transition-all"
                style={{ width: `${Math.min(100, (completedCount / ffa_quali_rounds) * 100)}%` }} />
            </div>

            <div className="space-y-3">
              {quali_rounds.map((r) => (
                <FFARoundCard
                  key={r.id}
                  tournamentId={tournament.id}
                  round={r}
                  phaseLabel="Quali"
                  participantTeams={teams}
                  onRefresh={loadFfa}
                  savingId={savingId}
                  onSaveScores={handleSaveScores}
                />
              ))}
            </div>

            {quali_standings.length > 0 && (
              <div className="card mt-1">
                <p className="text-xs font-bold text-slate-400 uppercase tracking-widest mb-3">
                  Qualifikations-Tabelle
                </p>
                <StandingsTable standings={quali_standings} finalistIds={finalistIds} />
              </div>
            )}

            {ffa_final_generated && (
              <div className="flex items-center gap-3 rounded-lg border-2 border-emerald-200 bg-emerald-50 p-3">
                <span className="text-2xl">🏆</span>
                <div>
                  <p className="font-semibold text-emerald-800 text-sm">Finale automatisch generiert!</p>
                  <p className="text-xs text-emerald-600 mt-0.5">
                    {finalists?.length || 8} Spieler qualifiziert
                    {finalists?.length > 8 ? ` (erweitert wegen Punktegleichstand auf Platz 8)` : ''}.
                  </p>
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* ── Phase 2: Finale ── */}
      {ffa_final_generated && final_round && (
        <div className="rounded-xl border border-sand-300 overflow-hidden">
          <div className="bg-sand-500 px-4 py-3 flex items-center justify-between">
            <span className="text-white font-bold text-sm">Phase 2 — Finale</span>
            <span className="text-sand-100 text-xs">Top {finalists?.length || 8} Spieler</span>
          </div>
          <div className="p-4 space-y-3 bg-white">
            {final_round.status !== 'completed' && (
              <div className="card bg-slate-50">
                <p className="text-xs font-bold text-slate-400 uppercase tracking-widest mb-2">Finalisten</p>
                <div className="flex flex-wrap gap-2">
                  {(finalists || []).map((s) => (
                    <span key={s.team_id}
                      className="text-xs px-2 py-1 rounded-full bg-ocean-50 text-ocean-700 border border-ocean-200 font-semibold">
                      {s.rank}. <TeamHoverName teamId={s.team_id} name={s.team_name} />
                    </span>
                  ))}
                </div>
              </div>
            )}

            <FFARoundCard
              tournamentId={tournament.id}
              round={final_round}
              phaseLabel="Finale"
              participantTeams={finalistTeams.length > 0 ? finalistTeams : teams}
              onRefresh={loadFfa}
              savingId={savingId}
              onSaveScores={handleSaveScores}
            />

            {final_standings.length > 0 && (
              <div className="card">
                <p className="text-xs font-bold text-slate-400 uppercase tracking-widest mb-3">
                  Finale-Ergebnis
                </p>
                <StandingsTable standings={final_standings} finalistIds={null} />
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

// ── Battle Royale Admin ─────────────────────────────────────────────────────────

const BR_PLACEMENT_POINTS = { 1: 10, 2: 6, 3: 5, 4: 4, 5: 3, 6: 2, 7: 1, 8: 1 };
function brPoints(placement, kills) {
  return (BR_PLACEMENT_POINTS[placement] || 0) + (parseInt(kills, 10) || 0);
}

const MAP_PRESETS = ['Erangel', 'Miramar', 'Sanhok', 'Vikendi'];

function BRStandingsTable({ standings }) {
  return (
    <table className="w-full text-sm">
      <thead>
        <tr className="text-xs text-slate-400 border-b border-slate-100">
          <th className="text-left py-1 pr-3 font-medium">#</th>
          <th className="text-left py-1 font-medium">Team</th>
          <th className="text-right py-1 font-medium">Punkte</th>
          <th className="text-right py-1 font-medium">Kills</th>
        </tr>
      </thead>
      <tbody>
        {standings.map((s) => (
          <tr key={s.team_id} className="border-b border-slate-50">
            <td className="py-1.5 pr-3 font-bold text-ocean-700">{s.rank}</td>
            <td className="py-1.5 font-medium text-slate-700"><TeamHoverName teamId={s.team_id} name={s.team_name} /></td>
            <td className="py-1.5 text-right font-bold text-sand-600">{s.total_points}</td>
            <td className="py-1.5 text-right text-slate-500">{s.total_kills}</td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

function MapInline({ round, onUpdated }) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(round.map_name || '');
  const [saving, setSaving] = useState(false);

  const save = async () => {
    setSaving(true);
    try { await onUpdated(round.id, draft.trim() || null); setEditing(false); }
    finally { setSaving(false); }
  };

  if (editing) {
    return (
      <div className="flex gap-1 items-center mt-1">
        <Input
          autoFocus value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onKeyDown={(e) => { if (e.key === 'Enter') save(); if (e.key === 'Escape') setEditing(false); }}
          placeholder="Map..."
          className="text-xs py-0.5 max-w-xs"
        />
        <button onClick={save} disabled={saving}
          className="text-xs bg-ocean-500 text-white px-2 py-1 rounded hover:bg-ocean-600 disabled:opacity-50">
          {saving ? '…' : '✓'}
        </button>
        <button onClick={() => { setDraft(round.map_name || ''); setEditing(false); }}
          className="text-xs text-slate-400 hover:text-slate-600 px-1">✕</button>
      </div>
    );
  }
  return (
    <div className="flex items-center gap-1 mt-1 cursor-pointer group" onClick={() => setEditing(true)}>
      {round.map_name
        ? <span className="text-xs text-slate-500 bg-slate-100 px-2 py-0.5 rounded group-hover:bg-slate-200 transition">🗺 {round.map_name}</span>
        : <span className="text-xs text-slate-300 group-hover:text-ocean-400 transition">+ Map festlegen</span>}
    </div>
  );
}

function BRScoreForm({ participantTeams, onSave, saving }) {
  const [entries, setEntries] = useState(() => {
    const init = {};
    participantTeams.forEach((t) => { init[t.id] = { placement: '', kills: '' }; });
    return init;
  });

  const setField = (teamId, field) => (e) =>
    setEntries({ ...entries, [teamId]: { ...entries[teamId], [field]: e.target.value } });

  const placementsUsed = Object.values(entries).map((e) => e.placement).filter((p) => p !== '');
  const hasDuplicates = placementsUsed.length !== new Set(placementsUsed).size;

  const submit = () => {
    const scores = participantTeams
      .filter((t) => entries[t.id].placement !== '')
      .map((t) => ({
        team_id: t.id,
        placement: parseInt(entries[t.id].placement, 10),
        kills: parseInt(entries[t.id].kills || 0, 10),
      }));
    onSave(scores);
  };

  return (
    <div className="mt-3 pt-3 border-t border-slate-100">
      <p className="text-xs font-semibold text-slate-500 mb-3 uppercase tracking-widest">Platzierung &amp; Kills eintragen</p>
      <div className="space-y-2 mb-4">
        {participantTeams.map((t) => {
          const e = entries[t.id];
          const pts = e.placement !== '' ? brPoints(e.placement, e.kills) : null;
          return (
            <div key={t.id} className="flex items-center gap-3">
              <span className="text-sm font-medium text-slate-700 w-36 truncate"><TeamHoverName team={t} /></span>
              <Input type="number" min="1" className="w-20 text-center" placeholder="Platz"
                value={e.placement} onChange={setField(t.id, 'placement')} />
              <Input type="number" min="0" className="w-20 text-center" placeholder="Kills"
                value={e.kills} onChange={setField(t.id, 'kills')} />
              {pts !== null && (
                <span className="text-xs text-slate-500">
                  → <span className="font-bold text-ocean-700">{pts} Pkt</span>
                </span>
              )}
            </div>
          );
        })}
      </div>
      {hasDuplicates && (
        <p className="text-xs text-red-500 mb-2">Jede Platzierung darf nur einmal vergeben werden.</p>
      )}
      <Button size="sm" loading={saving} disabled={hasDuplicates} onClick={submit}>Ergebnisse speichern</Button>
    </div>
  );
}

function BRRoundCard({ round, participantTeams, savingId, onSaveScores, onDeleteRound, onUpdateMap }) {
  const badge = round.status === 'completed'
    ? { label: 'Abgeschlossen', cls: 'bg-emerald-100 text-emerald-700' }
    : { label: 'Offen', cls: 'bg-amber-100 text-amber-700' };

  return (
    <div className={`card border ${round.status === 'open' ? 'border-amber-300 bg-amber-50/30' : 'border-slate-100'}`}>
      <div className="flex items-start justify-between gap-2 mb-1">
        <div>
          <div className="flex items-center gap-2">
            <span className="text-sm font-bold text-slate-700">Runde {round.round_number}</span>
            <span className={`text-[10px] font-bold uppercase px-2 py-0.5 rounded-full ${badge.cls}`}>{badge.label}</span>
          </div>
          <MapInline round={round} onUpdated={onUpdateMap} />
        </div>
        <div className="flex gap-2 shrink-0">
          {round.status === 'completed' && (
            <button
              onClick={() => { if (window.confirm('Ergebnisse dieser Runde zurücksetzen?')) onSaveScores(round.id, null); }}
              className="text-xs text-slate-400 hover:text-red-500 transition">
              Zurücksetzen
            </button>
          )}
          {round.scores.length === 0 && (
            <button
              onClick={() => onDeleteRound(round.id)}
              className="text-xs text-slate-400 hover:text-red-500 transition">
              Löschen
            </button>
          )}
        </div>
      </div>

      {round.status === 'completed' && round.scores.length > 0 && (
        <table className="w-full text-xs mt-2">
          <thead>
            <tr className="text-slate-400 border-b border-slate-100">
              <th className="text-left py-1 font-medium">Platz</th>
              <th className="text-left py-1 font-medium">Team</th>
              <th className="text-right py-1 font-medium">Kills</th>
              <th className="text-right py-1 font-medium">Punkte</th>
            </tr>
          </thead>
          <tbody>
            {round.scores.map((s) => (
              <tr key={s.team_id} className="border-b border-slate-50">
                <td className="py-1 font-bold text-slate-600">#{s.placement}</td>
                <td className="py-1 text-slate-700"><TeamHoverName teamId={s.team_id} name={s.team_name} /></td>
                <td className="py-1 text-right text-slate-600">{s.kills}</td>
                <td className="py-1 text-right font-bold text-ocean-600">{s.points}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      {round.status === 'open' && (
        <BRScoreForm
          participantTeams={participantTeams}
          saving={savingId === round.id}
          onSave={(scores) => onSaveScores(round.id, scores)}
        />
      )}
    </div>
  );
}

function BattleRoyaleAdmin({ tournament, teams, onRefresh }) {
  const [brData, setBrData] = useState(null);
  const [savingId, setSavingId] = useState(null);
  const [creating, setCreating] = useState(false);
  const [newMap, setNewMap] = useState('');
  const [error, setError] = useState('');

  const loadBr = useCallback(() => {
    api.get(`/api/tournaments/${tournament.id}/battle-royale`)
      .then((r) => setBrData(r.data))
      .catch(() => {});
  }, [tournament.id]);

  useEffect(() => { loadBr(); }, [loadBr]);

  const addRound = async () => {
    setCreating(true); setError('');
    try {
      await api.post(`/api/admin/tournaments/${tournament.id}/battle-royale/rounds`, { map_name: newMap.trim() || null });
      setNewMap('');
      loadBr(); onRefresh();
    } catch (err) { setError(err.response?.data?.detail || 'Fehler'); }
    finally { setCreating(false); }
  };

  const saveScores = async (roundId, scores) => {
    if (!scores) {
      if (!window.confirm('Ergebnisse zurücksetzen?')) return;
      try {
        await api.delete(`/api/admin/tournaments/${tournament.id}/battle-royale/rounds/${roundId}/scores`);
        loadBr(); onRefresh();
      } catch (err) { setError(err.response?.data?.detail || 'Fehler'); }
      return;
    }
    setSavingId(roundId); setError('');
    try {
      await api.post(`/api/admin/tournaments/${tournament.id}/battle-royale/rounds/${roundId}/scores`, { scores });
      loadBr(); onRefresh();
    } catch (err) { setError(err.response?.data?.detail || 'Fehler'); }
    finally { setSavingId(null); }
  };

  const updateMap = async (roundId, mapName) => {
    await api.put(`/api/admin/tournaments/${tournament.id}/battle-royale/rounds/${roundId}/map`, { map_name: mapName });
    loadBr();
  };

  const deleteRound = async (roundId) => {
    if (!window.confirm('Runde löschen?')) return;
    try {
      await api.delete(`/api/admin/tournaments/${tournament.id}/battle-royale/rounds/${roundId}`);
      loadBr(); onRefresh();
    } catch (err) { setError(err.response?.data?.detail || 'Fehler'); }
  };

  if (!brData) return <div className="text-slate-400 text-sm py-8 text-center">Lade...</div>;

  return (
    <div className="space-y-6">
      {error && <div className="alert-error">{error}</div>}

      <div className="card flex flex-wrap items-end gap-3">
        <FormField label="Neue Runde / Map" className="mb-0">
          <div className="flex gap-2">
            <Select value={MAP_PRESETS.includes(newMap) ? newMap : ''} onChange={(e) => setNewMap(e.target.value)} className="max-w-[160px]">
              <option value="">– Map wählen –</option>
              {MAP_PRESETS.map((m) => <option key={m} value={m}>{m}</option>)}
            </Select>
            <Input value={newMap} onChange={(e) => setNewMap(e.target.value)} placeholder="Eigene Map..." className="max-w-[160px]" />
          </div>
        </FormField>
        <Button size="sm" loading={creating} onClick={addRound}>+ Runde hinzufügen</Button>
      </div>

      {brData.rounds.length === 0 && (
        <div className="card border-2 border-dashed border-slate-200 text-center py-6 text-sm text-slate-400">
          Noch keine Runden. Lege oben die erste Map an.
        </div>
      )}

      <div className="space-y-3">
        {brData.rounds.map((r) => (
          <BRRoundCard
            key={r.id}
            round={r}
            participantTeams={teams}
            savingId={savingId}
            onSaveScores={saveScores}
            onDeleteRound={deleteRound}
            onUpdateMap={updateMap}
          />
        ))}
      </div>

      {brData.standings.length > 0 && (
        <div className="card">
          <p className="text-xs font-bold text-slate-400 uppercase tracking-widest mb-3">Gesamtwertung</p>
          <BRStandingsTable standings={brData.standings} />
        </div>
      )}
    </div>
  );
}

// ── Seeding Admin ──────────────────────────────────────────────────────────────

function SeedingAdmin({ tournament, teams, onRefresh, onGenerateBracket, generating }) {
  const [scores, setScores] = useState({});
  const [saved, setSaved] = useState([]);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [msg, setMsg] = useState('');

  useEffect(() => {
    api.get(`/api/tournaments/${tournament.id}/seeding`)
      .then((r) => {
        setSaved(r.data);
        const init = {};
        r.data.forEach((e) => { init[e.team_id] = e.score; });
        setScores(init);
      })
      .catch(() => {});
  }, [tournament.id]);

  useEffect(() => {
    const init = {};
    teams.forEach((t) => { if (!(t.id in scores)) init[t.id] = ''; });
    if (Object.keys(init).length) setScores((prev) => ({ ...prev, ...init }));
  }, [teams]); // eslint-disable-line

  const ranked = [...teams]
    .filter((t) => scores[t.id] !== '')
    .sort((a, b) => parseInt(scores[b.id] || 0) - parseInt(scores[a.id] || 0));

  const saveSeeding = async () => {
    const entries = teams.map((t) => ({ team_id: t.id, score: parseInt(scores[t.id] || 0) }));
    setSaving(true); setError('');
    try {
      await api.post(`/api/admin/tournaments/${tournament.id}/seeding/scores`, entries);
      setMsg('Seeding gespeichert!');
      const r = await api.get(`/api/tournaments/${tournament.id}/seeding`);
      setSaved(r.data);
    } catch (err) { setError(err.response?.data?.detail || 'Fehler'); }
    finally { setSaving(false); }
  };

  const hasSeedingDone = saved.length > 0 && saved.every((e) => e.seed_rank);

  return (
    <div>
      <div className="flex items-center justify-between mb-4">
        <h3 className="section-title mb-0">Phase 1 — Seeding</h3>
        {!tournament.bracket_generated && hasSeedingDone && (
          <Button onClick={onGenerateBracket} loading={generating}>
            Bracket aus Seeding generieren
          </Button>
        )}
      </div>

      {error && <div className="alert-error mb-3">{error}</div>}
      {msg && <div className="alert-success mb-3">{msg}</div>}

      {!tournament.bracket_generated ? (
        <>
          <p className="text-xs text-slate-500 mb-4">
            Trage die Ergebnisse (Frags/Punkte) der Seeding-Runde ein.
            Die Spieler werden automatisch nach Score gereiht — Platz 1 und 2 treffen sich erst im Finale.
          </p>
          <div className="space-y-2 mb-4">
            {teams.map((t) => {
              const rank = ranked.findIndex((r) => r.id === t.id) + 1 || null;
              return (
                <div key={t.id} className="flex items-center gap-3">
                  <span className="text-sm font-medium text-slate-700 w-36 truncate"><TeamHoverName team={t} /></span>
                  <Input
                    type="number" min="0" className="w-24 text-center"
                    placeholder="Score"
                    value={scores[t.id] ?? ''}
                    onChange={(e) => setScores({ ...scores, [t.id]: e.target.value })}
                  />
                  {rank && <span className="text-xs text-slate-400">Seed <span className="font-bold text-ocean-700">#{rank}</span></span>}
                </div>
              );
            })}
          </div>
          <Button size="sm" loading={saving} onClick={saveSeeding}>Seeding speichern</Button>
        </>
      ) : (
        <div className="card">
          <p className="text-xs font-bold text-slate-400 uppercase tracking-widest mb-3">Seeding-Ergebnis</p>
          <div className="space-y-1.5">
            {saved.sort((a, b) => (a.seed_rank || 99) - (b.seed_rank || 99)).map((e) => (
              <div key={e.team_id} className="flex items-center gap-3 text-sm">
                <span className="w-7 text-ocean-700 font-bold">#{e.seed_rank}</span>
                <span className="font-medium text-slate-800"><TeamHoverName teamId={e.team_id} name={e.team_name} /></span>
                <span className="text-slate-400 text-xs ml-auto">{e.score} Pkt</span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

// ── Team-Zuteilung Admin (admin_assigned Turniere) ────────────────────────────

function TeamAssignmentAdmin({ tournamentId, tournament, teams, onRefresh, onUnassignMember, onDeleteTeam, setError, setMessage }) {
  const [waitlist, setWaitlist] = useState([]);
  const [loadingWaitlist, setLoadingWaitlist] = useState(true);
  const [newTeamName, setNewTeamName] = useState('');
  const [creating, setCreating] = useState(false);
  const [assigning, setAssigning] = useState(null); // waitlist entry id currently being assigned
  const [targetTeamId, setTargetTeamId] = useState({}); // entryId -> teamId

  const loadWaitlist = useCallback(() => {
    setLoadingWaitlist(true);
    api.get(`/api/admin/tournaments/${tournamentId}/waitlist`)
      .then((r) => setWaitlist(r.data))
      .catch(() => setWaitlist([]))
      .finally(() => setLoadingWaitlist(false));
  }, [tournamentId]);

  useEffect(() => { loadWaitlist(); }, [loadWaitlist, teams]);

  const createTeam = async (e) => {
    e.preventDefault();
    if (!newTeamName.trim()) return;
    setCreating(true); setError(''); setMessage('');
    try {
      await api.post(`/api/admin/tournaments/${tournamentId}/teams`, { name: newTeamName.trim() });
      setNewTeamName('');
      onRefresh();
    } catch (err) { setError(err.response?.data?.detail || 'Fehler'); }
    finally { setCreating(false); }
  };

  const assign = async (entry) => {
    const teamId = targetTeamId[entry.id];
    if (!teamId) { setError('Bitte zuerst ein Team auswählen'); return; }
    setAssigning(entry.id); setError(''); setMessage('');
    try {
      await api.post(`/api/admin/teams/${teamId}/assign/${entry.user_id}`);
      setMessage(`${entry.username} zugeteilt`);
      loadWaitlist();
      onRefresh();
    } catch (err) { setError(err.response?.data?.detail || 'Fehler'); }
    finally { setAssigning(null); }
  };

  const nonFullTeams = teams.filter((t) => !t.is_full && t.status !== 'locked');

  return (
    <div className="space-y-6">
      <div className="card border border-amber-200 bg-amber-50/40">
        <p className="text-sm text-amber-800">
          Dieses Turnier nutzt <span className="font-bold">Admin-Team-Zuteilung</span>: Teilnehmer melden sich einzeln an
          und werden hier manuell den Teams zugeteilt.
        </p>
      </div>

      {/* Neues Team anlegen */}
      <div className="card">
        <h3 className="section-title mb-3">Neues Team anlegen</h3>
        <form onSubmit={createTeam} className="flex gap-3 flex-wrap items-end">
          <FormField label="Team-Name" className="flex-1 min-w-[200px]">
            <Input value={newTeamName} onChange={(e) => setNewTeamName(e.target.value)} placeholder="z.B. Team Alpha" required />
          </FormField>
          <Button type="submit" loading={creating}>Team anlegen</Button>
        </form>
      </div>

      {/* Warteliste */}
      <div className="card">
        <h3 className="section-title mb-3">Warteliste ({waitlist.length})</h3>
        {loadingWaitlist ? (
          <p className="text-sm text-slate-400">Lädt...</p>
        ) : waitlist.length === 0 ? (
          <p className="text-sm text-slate-400">Keine wartenden Anmeldungen.</p>
        ) : nonFullTeams.length === 0 ? (
          <p className="text-sm text-amber-600">
            {waitlist.length} Teilnehmer warten, aber es gibt noch kein offenes Team. Lege oben ein Team an.
          </p>
        ) : (
          <div className="space-y-2">
            {waitlist.map((entry) => (
              <div key={entry.id} className="flex items-center justify-between gap-3 text-sm py-1.5 border-b border-slate-50 last:border-b-0 flex-wrap">
                <span className="font-medium text-slate-700">
                  {entry.username}{entry.nickname ? ` (${entry.nickname})` : ''}
                </span>
                <div className="flex items-center gap-2">
                  <Select
                    value={targetTeamId[entry.id] || ''}
                    onChange={(e) => setTargetTeamId({ ...targetTeamId, [entry.id]: e.target.value })}
                    className="!py-1 !text-sm w-48"
                  >
                    <option value="">– Team wählen –</option>
                    {nonFullTeams.map((t) => (
                      <option key={t.id} value={t.id}>
                        {t.name} ({(t.members || []).length}/{tournament.team_size})
                      </option>
                    ))}
                  </Select>
                  <Button size="sm" loading={assigning === entry.id} onClick={() => assign(entry)}>
                    Zuteilen
                  </Button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Teams mit Zuteilung */}
      <div className="space-y-3">
        {teams.length === 0
          ? <div className="card text-center py-8 text-slate-400">Noch keine Teams angelegt.</div>
          : teams.map((team) => (
            <AdminAssignedTeamCard
              key={team.id}
              team={team}
              tournament={tournament}
              onUnassignMember={onUnassignMember}
              onDeleteTeam={onDeleteTeam}
            />
          ))}
      </div>
    </div>
  );
}

function AdminAssignedTeamCard({ team, tournament, onUnassignMember, onDeleteTeam }) {
  const members = team.members || [];
  const isFull = team.is_full;
  return (
    <div className={`card border-l-4 ${isFull ? 'border-l-emerald-400' : 'border-l-slate-300'}`}>
      <div className="flex justify-between items-start mb-2 flex-wrap gap-2">
        <div>
          <span className="font-bold text-slate-800"><TeamHoverName team={team} /></span>
          <span className={`ml-2 text-xs px-2 py-0.5 rounded-full border font-semibold ${
            isFull ? 'bg-emerald-50 text-emerald-700 border-emerald-200' : 'bg-slate-50 text-slate-500 border-slate-200'
          }`}>
            {members.length}/{tournament?.team_size || '?'} Spieler{isFull ? ' – Voll' : ''}
          </span>
          {team.status === 'locked' && (
            <span className="ml-1 text-xs px-2 py-0.5 rounded-full bg-slate-100 text-slate-500 border border-slate-200 font-semibold">
              Gesperrt (Bracket aktiv)
            </span>
          )}
        </div>
        {members.length === 0 && team.status !== 'locked' && (
          <Button size="sm" variant="danger" onClick={() => onDeleteTeam(team.id)}>Team löschen</Button>
        )}
      </div>
      {members.length === 0 ? (
        <p className="text-xs text-slate-400">Noch keine Spieler zugeteilt.</p>
      ) : (
        <div className="space-y-1">
          {members.map((m) => (
            <div key={m.id} className="flex items-center justify-between text-sm py-1 border-b border-slate-50 last:border-b-0">
              <span className="flex items-center gap-2">
                <Link to={`/users/${m.user_id}`} className="font-medium text-slate-700 hover:text-ocean-600 hover:underline">
                  {m.user?.username}
                </Link>
                {m.role === 'creator' && <span className="text-[10px] text-ocean-500 font-bold">Captain</span>}
              </span>
              {team.status !== 'locked' && (
                <Button size="sm" variant="danger" onClick={() => onUnassignMember(team.id, m.user_id)}>✕</Button>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ── Main component ──────────────────────────────────────────────────────────────

function TeamCard({ team, tournament, onRemoveMember, onDeleteTeam }) {
  const confirmed = (team.members || []).filter((m) => m.status === 'confirmed');
  const pending = (team.members || []).filter((m) => m.status === 'pending');
  const isFull = team.is_full;
  const canManage = !tournament?.bracket_generated;
  return (
    <div className={`card border-l-4 ${isFull ? 'border-l-emerald-400' : 'border-l-slate-300'}`}>
      <div className="flex justify-between items-start mb-2 flex-wrap gap-2">
        <div>
          <span className="font-bold text-slate-800"><TeamHoverName team={team} /></span>
          <span className={`ml-2 text-xs px-2 py-0.5 rounded-full border font-semibold ${
            isFull ? 'bg-emerald-50 text-emerald-700 border-emerald-200' : 'bg-slate-50 text-slate-500 border-slate-200'
          }`}>
            {confirmed.length}/{tournament?.team_size || '?'} Spieler{isFull ? ' – Voll' : ''}
          </span>
          {pending.length > 0 && (
            <span className="ml-1 text-xs px-2 py-0.5 rounded-full bg-amber-50 text-amber-700 border border-amber-200 font-semibold">
              {pending.length} ausstehend
            </span>
          )}
        </div>
        {canManage && confirmed.length === 0 && pending.length === 0 && (
          <Button size="sm" variant="danger" onClick={() => onDeleteTeam(team.id)}>Team löschen</Button>
        )}
      </div>
      <div className="space-y-1">
        {[...confirmed, ...pending].map((m) => (
          <div key={m.id} className="flex items-center justify-between text-sm py-1 border-b border-slate-50 last:border-b-0">
            <span className="flex items-center gap-2">
              <span className={`text-[10px] px-1.5 py-0.5 rounded font-bold ${
                m.status === 'confirmed' ? 'bg-emerald-50 text-emerald-600' : 'bg-amber-50 text-amber-600'
              }`}>{m.status === 'confirmed' ? 'OK' : 'PENDING'}</span>
              <Link to={`/users/${m.user_id}`} className="font-medium text-slate-700 hover:text-ocean-600 hover:underline">
                {m.user?.username}
              </Link>
              {m.role === 'creator' && <span className="text-[10px] text-ocean-500 font-bold">Captain</span>}
            </span>
            {canManage && (
              <Button size="sm" variant="danger" onClick={() => onRemoveMember(team.id, m.user_id, m.role === 'creator')}>✕</Button>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}

function RoundNoteEditor({ tournamentId, roundNumber, bracketSide, initialNote, onSaved }) {
  const [note, setNote] = useState(initialNote || '');
  const [saving, setSaving] = useState(false);
  const save = async () => {
    setSaving(true);
    try {
      await api.put(`/api/admin/tournaments/${tournamentId}/round-notes`, {
        round_number: roundNumber, bracket_side: bracketSide, note,
      });
      onSaved();
    } finally { setSaving(false); }
  };
  return (
    <div className="flex items-center gap-2 mt-1">
      <Input
        value={note}
        onChange={(e) => setNote(e.target.value)}
        placeholder="Notiz / Map eintragen..."
        className="flex-1 text-xs py-1"
        onKeyDown={(e) => e.key === 'Enter' && save()}
      />
      <Button size="sm" variant="ghost" loading={saving} onClick={save}>Speichern</Button>
    </div>
  );
}

function MatchesTab({ matches, roundNotes, tournamentId, onSaveResult, onNoteSaved }) {
  // Build note lookup: key = "round_bracket_side"
  const noteMap = {};
  (roundNotes || []).forEach((n) => { noteMap[`${n.round_number}_${n.bracket_side}`] = n.note; });

  // Group matches by (round, bracket_side), skip bye
  const nonBye = matches.filter((m) => m.status !== 'bye');
  const groups = [];
  const seen = new Set();
  nonBye.forEach((m) => {
    const key = `${m.round}_${m.bracket_side}`;
    if (!seen.has(key)) { seen.add(key); groups.push({ round: m.round, side: m.bracket_side, key }); }
  });
  groups.sort((a, b) => {
    const sideOrder = { winners: 0, losers: 1, third_place: 2, grand_final: 3, grand_final_reset: 4 };
    return (sideOrder[a.side] ?? 9) - (sideOrder[b.side] ?? 9) || a.round - b.round;
  });

  // Determine final round number for winners bracket to compute semantic names
  const winnerRounds = groups.filter((g) => g.side === 'winners').map((g) => g.round);
  const finalRound = winnerRounds.length > 0 ? Math.max(...winnerRounds) : 0;

  if (nonBye.length === 0) {
    return <div className="card text-center py-8 text-slate-400">Bracket noch nicht generiert.</div>;
  }

  return (
    <div className="space-y-4">
      {groups.map(({ round, side, key }) => {
        const groupMatches = nonBye.filter((m) => m.round === round && m.bracket_side === side);
        const currentNote = noteMap[key] || '';
        const label = getRoundLabel(round, side, finalRound);
        return (
          <div key={key} className="card">
            <div className="flex items-center gap-3 mb-2">
              <span className="text-sm font-bold text-ocean-700">{label}</span>
              {currentNote && (
                <span className="text-xs bg-slate-100 text-slate-600 px-2 py-0.5 rounded">
                  🗺 {currentNote}
                </span>
              )}
            </div>
            <RoundNoteEditor
              tournamentId={tournamentId}
              roundNumber={round}
              bracketSide={side}
              initialNote={currentNote}
              onSaved={onNoteSaved}
            />
            <div className="mt-3 space-y-2">
              {groupMatches.map((m) => (
                <div key={m.id} className="border border-slate-100 rounded-lg p-3">
                  <div className="flex items-start justify-between flex-wrap gap-2">
                    <div className="text-sm">
                      <span className="font-semibold text-slate-600 text-xs">M{m.match_number}</span>
                      <div className="mt-0.5 text-slate-700">
                        <span className={m.winner_id === m.team1_id ? 'font-bold text-emerald-600' : ''}>
                          {m.team1_id ? <TeamHoverName teamId={m.team1_id} name={m.team1?.name} /> : 'TBD'}
                        </span>
                        {m.score_team1 !== null && <span className="mx-1 font-bold">{m.score_team1}</span>}
                        <span className="text-slate-300 mx-1">:</span>
                        {m.score_team2 !== null && <span className="mx-1 font-bold">{m.score_team2}</span>}
                        <span className={m.winner_id === m.team2_id ? 'font-bold text-emerald-600' : ''}>
                          {m.team2_id ? <TeamHoverName teamId={m.team2_id} name={m.team2?.name} /> : 'TBD'}
                        </span>
                      </div>
                    </div>
                    <span className={`text-xs font-semibold px-2 py-0.5 rounded border ${
                      m.status === 'completed' ? 'bg-emerald-50 text-emerald-700 border-emerald-200' :
                      m.status === 'pending_confirmation' ? 'bg-amber-50 text-amber-700 border-amber-200' :
                      'bg-slate-50 text-slate-500 border-slate-200'
                    }`}>
                      {m.status === 'scheduled' ? 'Offen' : m.status === 'pending_confirmation' ? 'Ausstehend' : 'Fertig'}
                    </span>
                  </div>
                  <MatchResultEditor match={m} onSave={onSaveResult} />
                </div>
              ))}
            </div>
          </div>
        );
      })}
    </div>
  );
}

function MatchResultEditor({ match, onSave }) {
  const [s1, setS1] = useState(match.score_team1 ?? '');
  const [s2, setS2] = useState(match.score_team2 ?? '');
  const [saving, setSaving] = useState(false);
  if (match.status === 'bye' || !match.team1_id || !match.team2_id) return null;
  return (
    <form onSubmit={async (e) => {
      e.preventDefault(); setSaving(true);
      try { await onSave(match.id, parseInt(s1), parseInt(s2)); }
      finally { setSaving(false); }
    }} className="flex items-center gap-2 mt-2 flex-wrap">
      <Input type="number" min="0" value={s1} onChange={(e) => setS1(e.target.value)} className="w-16 text-center" required />
      <span className="text-slate-300 font-bold">:</span>
      <Input type="number" min="0" value={s2} onChange={(e) => setS2(e.target.value)} className="w-16 text-center" required />
      <Button type="submit" size="sm" loading={saving}>Setzen</Button>
    </form>
  );
}

export default function AdminTournamentDetail() {
  const { id } = useParams();
  const { user } = useAuth();
  const [tournament, setTournament] = useState(null);
  const [bracket, setBracket] = useState(null);
  const [teams, setTeams] = useState([]);
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');
  const [activeTab, setActiveTab] = useState('main');
  const [selectedMatch, setSelectedMatch] = useState(null);

  const isFFA = tournament?.format === 'ffa_scoreboard';
  const isSeeded = tournament?.format === 'seeded_single_elimination';
  const isBR = tournament?.format === 'battle_royale';
  const isTimeTrial = tournament?.format === 'time_trial';

  const load = useCallback(() => {
    Promise.all([
      api.get(`/api/tournaments/${id}`),
      api.get(`/api/tournaments/${id}/bracket`),
      api.get(`/api/admin/tournaments/${id}/teams`),
    ]).then(([tRes, bRes, teamsRes]) => {
      setTournament(tRes.data);
      setBracket(bRes.data);
      setTeams(teamsRes.data);
      if (tRes.data.format === 'time_trial') setActiveTab('info');
    }).finally(() => setLoading(false));
  }, [id]);

  useEffect(() => { load(); }, [load]);

  const generateBracket = async () => {
    if (!window.confirm('Bracket generieren? Danach können keine Teams mehr beitreten.')) return;
    setGenerating(true); setError(''); setMessage('');
    try {
      const res = await api.post(`/api/admin/tournaments/${id}/generate-bracket`);
      setMessage(res.data.message);
      load();
    } catch (err) { setError(err.response?.data?.detail || 'Fehler'); }
    finally { setGenerating(false); }
  };

  const setResult = async (matchId, s1, s2) => {
    setError(''); setMessage('');
    try {
      await api.put(`/api/admin/matches/${matchId}/result`, { score_team1: s1, score_team2: s2 });
      setMessage('Ergebnis gesetzt');
      load();
    } catch (err) { setError(err.response?.data?.detail || 'Fehler'); }
  };

  const removeMember = async (teamId, userId, isCaptain) => {
    const msg = isCaptain
      ? 'Captain abmelden? Ein anderes Teammitglied wird automatisch neuer Captain.'
      : 'Spieler aus Team entfernen?';
    if (!window.confirm(msg)) return;
    try { await api.delete(`/api/teams/${teamId}/members/${userId}`); load(); }
    catch (err) { setError(err.response?.data?.detail || 'Fehler'); }
  };

  const deleteTeam = async (teamId) => {
    if (!window.confirm('Leeres Team löschen?')) return;
    try { await api.delete(`/api/teams/${teamId}`); load(); }
    catch (err) { setError(err.response?.data?.detail || 'Fehler'); }
  };

  const unassignMember = async (teamId, userId) => {
    if (!window.confirm('Spieler aus Team entfernen und zurück auf die Warteliste legen?')) return;
    try { await api.post(`/api/admin/teams/${teamId}/unassign/${userId}`); load(); }
    catch (err) { setError(err.response?.data?.detail || 'Fehler'); }
  };

  const deleteAdminTeam = async (teamId) => {
    if (!window.confirm('Team löschen? Zugewiesene Spieler wandern zurück auf die Warteliste.')) return;
    try { await api.delete(`/api/admin/teams/${teamId}`); load(); }
    catch (err) { setError(err.response?.data?.detail || 'Fehler'); }
  };

  if (loading) return (
    <div className="flex justify-center py-16">
      <div className="animate-spin rounded-full h-8 w-8 border-2 border-ocean-400 border-t-transparent" />
    </div>
  );
  if (!tournament) return null;

  const matches = bracket?.matches || [];
  const fullTeams = teams.filter((t) => t.is_full || t.status === 'full' || t.status === 'locked');

  const tabs = isTimeTrial
    ? [{ key: 'info', label: 'Info' }]
    : isFFA
    ? [{ key: 'main', label: 'FFA-Runden' }, { key: 'teams', label: `Teams (${teams.length})` }]
    : isBR
    ? [{ key: 'main', label: 'Battle-Royale-Runden' }, { key: 'teams', label: `Teams (${teams.length})` }]
    : isSeeded
    ? [
        { key: 'main', label: 'Seeding' },
        { key: 'bracket', label: 'Turnierbaum' },
        { key: 'teams', label: `Teams (${teams.length})` },
        { key: 'matches', label: `Matches (${matches.length})` },
      ]
    : [
        { key: 'bracket', label: 'Turnierbaum' },
        { key: 'teams', label: `Teams (${teams.length})` },
        { key: 'matches', label: `Matches (${matches.length})` },
      ];

  return (
    <div>
      <div className="flex items-center gap-2 mb-1">
        <Link to="/admin/tournaments" className="text-sm text-ocean-600 hover:underline">← Turniere</Link>
      </div>
      <div className="flex items-start justify-between flex-wrap gap-3 mb-6">
        <div>
          <h2 className="page-title">{tournament.name}</h2>
          <div className="flex flex-wrap gap-3 text-sm text-slate-500 mt-1">
            {tournament.game && <span className="font-medium text-ocean-600">{tournament.game}</span>}
            <span className="inline-flex items-center gap-1.5">
              {FORMAT_LABELS[tournament.format]}
              <Tooltip text={FORMAT_DESCRIPTIONS[tournament.format]} />
            </span>
            {!isFFA && !isBR && !isTimeTrial && <span>{tournament.team_size}v{tournament.team_size}</span>}
            {isBR && <span>{tournament.team_size} Spieler/Team</span>}
            {!isTimeTrial && <span>{tournament.teams_registered} Teams ({tournament.teams_full} voll)</span>}
          </div>
        </div>
        {!isFFA && !isBR && !isTimeTrial && !tournament.bracket_generated && !isSeeded && (
          <Button onClick={generateBracket} loading={generating} disabled={fullTeams.length < 2}>
            Bracket generieren ({fullTeams.length} Teams)
          </Button>
        )}
        {tournament.bracket_generated && (
          <span className="text-xs font-semibold bg-ocean-50 text-ocean-700 border border-ocean-200 px-3 py-1.5 rounded-lg">
            Bracket aktiv
          </span>
        )}
      </div>

      {message && <div className="alert-success mb-4">{message}</div>}
      {error && <div className="alert-error mb-4">{error}</div>}

      {/* Tabs */}
      <div className="flex gap-1 border-b border-slate-200 mb-6">
        {tabs.map((tab) => (
          <button key={tab.key} onClick={() => setActiveTab(tab.key)}
            className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
              activeTab === tab.key ? 'border-ocean-500 text-ocean-700' : 'border-transparent text-slate-500 hover:text-ocean-600'
            }`}>
            {tab.label}
          </button>
        ))}
      </div>

      {/* FFA main tab */}
      {activeTab === 'main' && isFFA && (
        <FFAAdmin tournament={tournament} teams={fullTeams} onRefresh={load} />
      )}

      {/* Battle Royale main tab */}
      {activeTab === 'main' && isBR && (
        <BattleRoyaleAdmin tournament={tournament} teams={fullTeams} onRefresh={load} />
      )}

      {/* Seeding main tab */}
      {activeTab === 'main' && isSeeded && (
        <SeedingAdmin
          tournament={tournament}
          teams={fullTeams}
          onRefresh={load}
          onGenerateBracket={generateBracket}
          generating={generating}
        />
      )}

      {/* Zeitfahren info tab */}
      {activeTab === 'info' && isTimeTrial && (
        <div className="card">
          <dl className="space-y-3 text-sm">
            {tournament.start_time && (
              <div className="flex justify-between border-b border-slate-100 pb-3">
                <dt className="text-slate-500 font-medium">Startzeit</dt>
                <dd className="font-semibold text-slate-800">
                  {new Date(tournament.start_time).toLocaleString('de-DE', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' })}
                </dd>
              </div>
            )}
            {tournament.description && (
              <div className={tournament.rules ? "border-b border-slate-100 pb-3" : ""}>
                <dt className="text-slate-500 font-medium mb-1">Beschreibung</dt>
                <dd className="text-slate-700 leading-relaxed whitespace-pre-wrap">{tournament.description}</dd>
              </div>
            )}
            {tournament.rules && (
              <div>
                <dt className="text-slate-500 font-medium mb-1">Spielregeln/Servereinstellungen</dt>
                <dd className="text-slate-700 leading-relaxed whitespace-pre-wrap">{tournament.rules}</dd>
              </div>
            )}
          </dl>
          <p className="text-xs text-slate-400 mt-4">
            Name, Spiel, Beschreibung, Spielregeln/Servereinstellungen und Startzeit lassen sich unter „Turniere" bearbeiten.
          </p>
        </div>
      )}

      {/* Bracket */}
      {activeTab === 'bracket' && (
        <BracketView
          tournament={tournament}
          matches={matches}
          teams={bracket?.teams || teams}
          roundNotes={bracket?.round_notes || []}
          onMatchClick={(match) => setSelectedMatch(match)}
          isAdmin={true}
          tournamentId={id}
          onNoteUpdated={load}
        />
      )}

      {selectedMatch && (
        <MatchModal
          match={selectedMatch}
          user={user}
          isAdmin={true}
          onClose={() => setSelectedMatch(null)}
          onRefresh={() => { load(); setSelectedMatch(null); }}
        />
      )}

      {/* Teams */}
      {activeTab === 'teams' && (
        tournament.team_assignment_mode === 'admin_assigned' ? (
          <TeamAssignmentAdmin
            tournamentId={id}
            tournament={tournament}
            teams={teams}
            onRefresh={load}
            onUnassignMember={unassignMember}
            onDeleteTeam={deleteAdminTeam}
            setError={setError}
            setMessage={setMessage}
          />
        ) : (
          <div className="space-y-3">
            {teams.length === 0
              ? <div className="card text-center py-8 text-slate-400">Noch keine Teams.</div>
              : teams.map((team) => (
                <TeamCard key={team.id} team={team} tournament={tournament} onRemoveMember={removeMember} onDeleteTeam={deleteTeam} />
              ))}
          </div>
        )
      )}

      {/* Matches */}
      {activeTab === 'matches' && (
        <MatchesTab
          matches={matches}
          roundNotes={bracket?.round_notes || []}
          tournamentId={id}
          onSaveResult={setResult}
          onNoteSaved={load}
        />
      )}
    </div>
  );
}
